import { api } from '../lib/api';

interface BookingData {
  id?: number;
  orgId: string;
  smLink: string;
  dateTime: string;
  trackableLink?: string;
  firstName: string;
  lastName: string;
  email?: string;
  closed: string;
  closeAmount: string;
  createdAt?: string;
}

interface VideoAnalyticsData {
  success: boolean;
  orgId: string;
  videoUrl: string;
  totalBookings: number;
  closedBookings: number;
  totalRevenue: number;
  conversionRate: number;
  last7DaysBookings: number;
  bookingsOverTime: Array<{ time: string; count: number }>;
  peakBookingTimes: {
    byDayOfWeek: Record<string, number>;
    byHourOfDay: Record<string, number>;
  };
}

export const bookingsService = {
  // Fetch bookings from Google Sheets (returns array format for legacy components)
  async fetchBookings(orgId?: string): Promise<any[][]> {
    try {
      const sheetsData = await api.sheets.fetchTrackedBookings();
      return sheetsData;
    } catch (error) {
      console.error('Error fetching bookings from sheets:', error);
      return [['OrgID', 'SM Link', 'Date/Time', 'First Name', 'Last Name', 'Email', 'Closed', 'Close Amount']];
    }
  },

  // Fetch bookings as structured data (database only - no Google Sheets fallback)
  async fetchBookingsData(orgId: string): Promise<BookingData[]> {
    try {
      const dbResponse = await api.bookings.fetchByOrgId(orgId);
      return dbResponse.bookings;
    } catch (error) {
      console.error('Error fetching bookings data:', error);
      throw error;
    }
  },

  // Get booking statistics
  async getBookingStats(orgId: string): Promise<{ totalBookings: number; closedBookings: number; totalRevenue: number }> {
    try {
      const dbResponse = await api.bookings.fetchByOrgId(orgId);
      return dbResponse.stats;
    } catch (error: any) {
      console.error('Error fetching booking stats:', error);
      return { totalBookings: 0, closedBookings: 0, totalRevenue: 0 };
    }
  },

  // Add a new booking
  async addBooking(booking: Omit<BookingData, 'id' | 'createdAt'>): Promise<any> {
    return await api.bookings.add(booking);
  },

  // Update booking closed status
  async updateBookingClosed(orgId: string, bookingId: number, closed: string, closeAmount: string): Promise<any> {
    try {
      const result = await api.bookings.updateClosed(orgId, bookingId, closed, closeAmount);
      return result;
    } catch (error) {
      throw error;
    }
  },

  // Delete a booking
  async deleteBooking(orgId: string, bookingId: number): Promise<any> {
    try {
      const result = await api.bookings.delete(orgId, bookingId);
      return result;
    } catch (error) {
      throw error;
    }
  },

  // Fetch video analytics
  async fetchVideoAnalytics(orgId: string, videoUrl: string, granularity: 'hourly' | 'daily' = 'daily'): Promise<VideoAnalyticsData> {
    try {
      const result = await api.bookings.fetchVideoAnalytics(orgId, videoUrl, granularity);
      return result;
    } catch (error) {
      console.error('Error fetching video analytics:', error);
      throw error;
    }
  },

  // Fetch bookings filtered by video URL
  async fetchBookingsByVideo(orgId: string, videoUrl: string, startDate?: string, endDate?: string): Promise<BookingData[]> {
    try {
      const result = await api.bookings.fetchByVideo(orgId, videoUrl, startDate, endDate);
      return result.bookings;
    } catch (error) {
      console.error('Error fetching bookings by video:', error);
      throw error;
    }
  }
};

export default bookingsService;
